import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_STRING as api } from '@/App';
import { toast } from 'sonner';
import { useDarkMode } from '@/hooks/useDarkMode';

import { logger } from '@/utils/logger';

import { ArrowLeft, ArrowRight, Loader2, Plus, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
// Review-and-edit screen for the AI-generated questionnaire. The user lands
// here after `POST /api/keepsakes`. Each question is editable; they can add,
// remove, or rewrite anything before saving and moving on to share the link.
export default function KeepsakeEditQuestions() {
  const darkMode = useDarkMode();
  const navigate = useNavigate();
  const { id } = useParams();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${api}/keepsakes/${id}`, { credentials: 'include' });
        if (!res.ok) throw new Error('Not found');
        const data = await res.json();
        if (cancelled) return;
        setQuestionnaire(data);
        setQuestions(data.questions || []);
      } catch (err) {
        logger.error(err);
        toast.error('Failed to load questionnaire');
        navigate('/keepsakes');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  const updateQuestion = (idx, value) => {
    setQuestions((prev) => prev.map((q, i) => (i === idx ? value : q)));
  };

  const addQuestion = () => {
    setQuestions((prev) => [...prev, '']);
  };

  const removeQuestion = (idx) => {
    setQuestions((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    const cleaned = questions.map((q) => q.trim()).filter(Boolean);
    if (cleaned.length === 0) {
      toast.error('Add at least one question before saving.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${api}/keepsakes/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questions: cleaned })
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('Saved! Time to share it.');
      navigate(`/keepsakes/${id}/questionnaire`);
    } catch (err) {
      logger.error(err);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: darkMode.bgPrimary }}>
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3" style={{ color: '#9E6752' }} />
          <p style={{ color: darkMode.textSecondary }}>Loading your questions…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: darkMode.bgPrimary }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          onClick={() => navigate('/keepsakes')}
          className="flex items-center gap-2 mb-6 text-sm hover:underline"
          style={{ color: '#9E6752' }}
          data-testid="keq-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Keepsakes
        </button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B', fontFamily: 'Playfair Display, serif' }}>
            Review {questionnaire?.recipient_name || 'your'} questionnaire
          </h1>
          <p className="text-base" style={{ color: darkMode.isDark ? '#E5E5E5' : '#73766A' }}>
            These were drafted from {questionnaire?.recipient_name || 'this person'}'s profile. Edit anything that doesn't sound quite right, then save to get a shareable link.
          </p>
        </div>

        {/* Info banner */}
        <div
          className="p-4 rounded-lg mb-6 flex gap-3"
          style={{
            background: darkMode.isDark ? 'rgba(254, 215, 165, 0.15)' : '#FEF3C7',
            border: `1px solid ${darkMode.isDark ? 'rgba(254, 215, 165, 0.3)' : 'transparent'}`
          }}
        >
          <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: darkMode.isDark ? '#FED7A5' : '#92400E' }} />
          <p className="text-sm leading-relaxed" style={{ color: darkMode.isDark ? '#FED7A5' : '#92400E' }}>
            <strong>Heads up:</strong> you can change anything below. Rewrite a question, remove ones you don't like, or add new ones. Nothing is locked in.
          </p>
        </div>

        <div className="space-y-4">
          {questions.map((q, idx) => (
            <div
              key={idx}
              className="rounded-xl p-4"
              style={{
                background: darkMode.isDark ? '#2D4354' : 'white',
                boxShadow: darkMode.isDark ? '0 2px 10px rgba(0,0,0,0.3)' : '0 2px 10px rgba(0,0,0,0.05)'
              }}
              data-testid={`keq-question-row-${idx}`}
            >
              <div className="flex items-start gap-3">
                <span
                  className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
                  style={{ background: '#9E6752', color: 'white' }}
                >
                  {idx + 1}
                </span>
                <Textarea
                  value={q}
                  onChange={(e) => updateQuestion(idx, e.target.value)}
                  rows={2}
                  className="flex-1 resize-y min-h-[64px]"
                  ref={(el) => {
                    // Auto-grow each textarea to fit its content so long
                    // questions don't clip behind the next card.
                    if (el) {
                      el.style.height = 'auto';
                      el.style.height = `${el.scrollHeight}px`;
                    }
                  }}
                  style={{
                    background: darkMode.isDark ? 'rgba(255,255,255,0.06)' : '#FAFAFA',
                    color: darkMode.isDark ? '#FFFAF5' : '#20212B',
                    border: `1px solid ${darkMode.isDark ? 'rgba(254, 215, 165, 0.3)' : '#E5E5E5'}`,
                    overflow: 'hidden'
                  }}
                  data-testid={`keq-question-input-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => removeQuestion(idx)}
                  className="p-2 rounded-lg transition-colors hover:bg-red-500/10 flex-shrink-0"
                  style={{ color: '#EF4444' }}
                  title="Remove this question"
                  data-testid={`keq-remove-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4">
          <Button
            type="button"
            onClick={addQuestion}
            variant="outline"
            className="w-full"
            style={{
              borderStyle: 'dashed',
              borderColor: darkMode.isDark ? 'rgba(254, 215, 165, 0.4)' : '#D1D5DB',
              color: darkMode.isDark ? '#FED7A5' : '#534145',
              background: 'transparent'
            }}
            data-testid="keq-add-question-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add another question
          </Button>
        </div>

        <div className="mt-8 flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/keepsakes')}
            className="flex-1"
            style={{
              background: darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white',
              color: darkMode.isDark ? '#FED7A5' : '#20212B',
              border: `1px solid ${darkMode.isDark ? '#FED7A5' : '#D1D5DB'}`
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex-1"
            style={{ background: '#9E6752', color: 'white' }}
            data-testid="keq-save-btn"
          >
            {saving ? 'Saving…' : (
              <>
                Save & Get Share Link
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
